import { createFileRoute, Link } from "@tanstack/react-router";
import { buildSeo } from "@/lib/seo";
import { Layout } from "@/components/Layout";
import { SEOHead } from "@/components/SEOHead";
import { Card, CardContent } from "@/components/ui/card";
import { Sparkles, CheckCircle, Calculator, BarChart3, Coins } from "lucide-react";

const tools = [
  { to: "/tools/number-generator" as const, icon: Sparkles, title: "Number Generator", desc: "Generate random or strategy-based lottery numbers for any game. Choose from Random, Hot Bias, Cold Bias, and more." },
  { to: "/tools/results-checker" as const, icon: CheckCircle, title: "Results Checker", desc: "Check your lottery numbers against the latest winning draws. See how many numbers you matched." },
  { to: "/tools/tax-calculator" as const, icon: Calculator, title: "Tax Calculator", desc: "Estimate your take-home amount after federal and state taxes on lottery winnings." },
  { to: "/tools/frequency-analyzer" as const, icon: BarChart3, title: "Frequency Analyzer", desc: "Analyze number frequency across recent draws. Discover hot, cold, and overdue numbers." },
  { to: "/tools/payout-calculator" as const, icon: Coins, title: "Payout Calculator", desc: "Compare lump-sum vs 30-year annuity payouts for any jackpot, by state and filing status." },
];

export const Route = createFileRoute("/tools/")({
  head: () => buildSeo({
    title: "Lottery Tools — Number Generator, Tax Calculator & More",
    description: "Free lottery tools for US lottery games: number generator, results checker, tax calculator, and frequency analyzer.",
    path: "/tools",
  }),
  component: ToolsHub,
});

function ToolsHub() {
  return (
    <Layout>
      <SEOHead title="Lottery Tools" description="Free lottery tools for US lottery games." />
      <div className="container py-8">
        <h1 className="font-sans text-[30px] font-bold leading-tight mb-6 tracking-tight">Free Lottery Tools and Calculators</h1>
        <h2 className="font-sans text-[20px] font-bold leading-7 mb-4 tracking-tight">Browse lottery tools</h2>
        <div className="grid gap-4 sm:grid-cols-2">
          {tools.map(({ to, icon: Icon, title, desc }) => (
            <Link key={to} to={to}>
              <Card className="hover:shadow-md transition-shadow h-full">
                <CardContent className="pt-6">
                  <Icon className="h-10 w-10 text-primary mb-4" strokeWidth={2.25} />
                  <h3 className="font-sans font-bold text-[18px] mb-2 tracking-tight">{title}</h3>
                  <p className="font-sans text-[15px] leading-6 text-muted-foreground">{desc}</p>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>
      </div>
    </Layout>
  );
}